import { Col, Row, Button, Modal, Form } from "react-bootstrap";
import { Fragment, useContext, useRef, useState } from "react";
import { abpnClient } from "../../../../Services/AbpnClient";
import { AuthContext } from "../../../Auth/AuthProvider";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import { ValidatorHelper } from "../../../Shared/ValidatorHelper";

const validEmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface IProps {
    hadCertificate: boolean;
}

export default function RequestCertificationVerification(props: IProps) {
    const authContext = useContext(AuthContext);

    const emailForm = useRef(null);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [showRequestSection, setShowRequestSection] = useState(false);
    const [showEmailSection, setShowEmailSection] = useState(false);
    const [newEmail, setNewEmail] = useState<string>("");
    const [ccEmailAddresses, setCcEmailAddresses] = useState<Array<string>>([]);

    const downloadVerification = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDownloading(true);

        var response = await abpnClient.getResponse("/api/physician/DownloadVerificationLetter", authContext)
        var blob = await response.blob();

        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );
        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute(
            'download',
            `VerificationLetter.pdf`,
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);

        setIsDownloading(false);
        closeModals();
    }

    const updateEmailAddress = (value: string) => {
        setNewEmail(value);
    }

    const addEmail = async (e: React.FormEvent<HTMLFormElement>) => {
        const form = emailForm.current;
        e.preventDefault();
        e.stopPropagation();

        if (!validEmailRegex.test(newEmail)) {
            ValidatorHelper.setCustomValidationWithFeedback(emailInputRef.current, "Please enter a valid email address");
        }
        else {
            ValidatorHelper.clearCustomValidation(emailInputRef.current);
        }

        form.classList.add('was-validated');
        if (form.checkValidity()) {
            form.classList.remove('was-validated');
            let updatedEmails = deepCopy(ccEmailAddresses);
            updatedEmails.push(newEmail);
            setNewEmail("");
            setCcEmailAddresses(updatedEmails);
        }
    }

    const emailVerification = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const form = emailForm.current;
        e.preventDefault();
        e.stopPropagation();

        if (ccEmailAddresses.length === 0) {
            ValidatorHelper.setCustomValidationWithFeedback(emailInputRef.current, "Please enter an email address");
            form.classList.add('was-validated');
        } else {
            ValidatorHelper.clearCustomValidation(emailInputRef.current);
            setIsSending(true);
            abpnClient.post<Array<string>>("/api/physician/SendVerificationLetter", ccEmailAddresses, authContext).then(_ => {
                form.classList.remove('was-validated');
                closeModals();
            }).finally(() => {
                setIsSending(false);
            });
        }
    }

    const openLetterRequestModal = async (isRequestingVerificationLetter: boolean) => {
        setShowRequestSection(isRequestingVerificationLetter);
    }

    const openEmailModal = async (isEmailingVerificationLetter: boolean) => {
        setShowEmailSection(isEmailingVerificationLetter);
    }

    const closeModals = async () => {
        setNewEmail("");
        setCcEmailAddresses([]);
        setShowRequestSection(false);
        setShowEmailSection(false);
    }

    let showVerificationButtons = props.hadCertificate;

    return (
        <>
            <Row className="mt-3">
                {showVerificationButtons &&
                    <Col>
                        <Row>
                            <Col>
                                <Button variant="pp-primary" onClick={() => openLetterRequestModal(true)} className="ml-1 w-100">Request Certification Verification</Button>
                            </Col>
                        </Row>
                    </Col>
                }
            </Row>
            <Modal show={showRequestSection} onHide={() => closeModals()}>
                <Modal.Body>
                    {isDownloading ? <AbpnSpinner /> :
                        <Row className="justify-content-between">
                            <Col>
                                <Button variant="pp-primary" onClick={(e) => downloadVerification(e)} className="m-1 p-1 w-100">Download Certification Verification</Button>
                            </Col>
                            <Col>
                                <Button variant="pp-primary" onClick={() => openEmailModal(true)} className="m-1 p-1 w-100">Email Certification Verification</Button>
                            </Col>
                        </Row>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-pp-secondary" onClick={() => closeModals()}>
                        No, Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showEmailSection} onHide={() => closeModals()}>
                <Modal.Body>
                    <div className="mb-2">
                        If you would like the letter of certification verification emailed to a
                        third party, enter the email address and select the plus/add button.
                        You may add up to 3 different email addresses.  After you have
                        added the email address(es), select the Send Email button.  Emails
                        will send TO the email address we have on file for you and
                        CC the third party email address(es) indicated.
                    </div>
                    <Form noValidate ref={emailForm} onSubmit={(e) => addEmail(e)}>
                        <div>
                            <Row className="mb-4" hidden={ccEmailAddresses.length >= 3}>
                                <Col md={10}>
                                    <Form.Control type="email"
                                        required
                                        ref={emailInputRef}
                                        onChange={(e) => updateEmailAddress(e.target.value)}
                                        value={newEmail} />
                                    <span className="invalid-feedback"></span>
                                </Col>
                                <Col md={1}>
                                    <Button className="font-weight-bold" variant="pp-primary" type="submit"><i className="la la-plus"></i></Button>
                                </Col>
                            </Row>
                        </div>
                        {ccEmailAddresses.length > 0 &&
                            <Fragment>
                                <strong>Send to: </strong>
                                <ul>
                                    {ccEmailAddresses.map((emailAddress, index) =>
                                        <li key={index}>{emailAddress}</li>
                                    )}
                                </ul>
                            </Fragment>}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    {isSending ? <AbpnSpinner /> :
                        <>
                            <Button variant="outline-pp-primary" className="me-4" onClick={(e) => emailVerification(e)}>
                                Send Email
                            </Button>
                            <Button variant="outline-pp-secondary" onClick={() => closeModals()}>
                                No, Cancel
                            </Button>
                        </>
                    }
                </Modal.Footer>
            </Modal>
        </>
    )
}